import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { SCROLL_OFFSET } from 'site-modules/shared/constants/sub-navigation';
import { MENU_HASH } from 'site-modules/shared/constants/core-page/menu-hash';
import { getEditorialRatingParams } from 'site-modules/shared/utils/core-page/core-highlights';
import { ScrollLink } from 'site-modules/shared/components/scroll-link/scroll-link';
import { PopoverItem } from 'site-modules/shared/components/popover-item/popover-item';

import './rating-badge.scss';

// TODO: Remove when 2026 becomes used
export function hasExplanation({ isUsed, year }) {
  return !isUsed && ['2025', '2026'].includes(`${year}`);
}

export function RatingBadge({
  className = null,
  badgeContainerClassName = null,
  editorialRating = null,
  ratingScale = 10,
  isScrollLink = false,
  hideRatingText = false,
  hideTestedText = false,
  showExplanationTooltip = false,
  ratingVersion = null,
  isScorecardV4 = false,
}) {
  if (!editorialRating) {
    return null;
  }

  const isTenScale = ratingScale === 10;
  const { text, badgeClassName } = getEditorialRatingParams(editorialRating, { isScorecardV4, ratingVersion });

  const Tag = isScrollLink ? ScrollLink : Fragment;
  const tagProps = isScrollLink
    ? {
        className: 'd-flex flex-column align-items-center clickable',
        to: MENU_HASH.RATING_SCORECARD,
        scrollConfig: { offset: SCROLL_OFFSET },
        'data-tracking-id': 'core_page_view_edmunds_review',
        'data-tracking-value': `${editorialRating}/${ratingScale}`,
      }
    : {};

  return (
    <div className={classnames('rating-badge rounded-8 d-flex flex-column align-items-center', className)}>
      {isTenScale && !hideRatingText && (
        <div className="pos-r fw-bold text-nowrap mb-0_25">
          {text}{' '}
          {showExplanationTooltip && (
            <PopoverItem
              screenReaderTooltipLabel="our rating"
              className="rating-popover pos-a center-y right-0"
              openTrackingParams={{ subaction_name: 'view_tooltip_rating_explanation', value: text }}
            >
              In 2025, our testing team gave our scoring system a tune-up. You may notice some scores appear lower than
              before. We&apos;re being a bit tougher with our numbers to ensure you get the most accurate and honest
              evaluation possible. We know there&apos;s no such thing as a &apos;perfect&apos; car, which is why you
              won&apos;t see any get a 10 out of 10. But our goal is the same: to help you find a car perfect for you.
              Same trusted advice, just a little sharper.
            </PopoverItem>
          )}
        </div>
      )}
      <Tag {...tagProps}>
        <div
          className={classnames('d-flex pos-r text-center', badgeContainerClassName, { 'mb-0_25': !hideTestedText })}
        >
          <svg
            className={classnames('badge-img', {
              'scorecard-v4': isScorecardV4,
              [badgeClassName]: isTenScale,
            })}
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 70 76"
            width={70}
            height={76}
            fill="none"
            preserveAspectRatio="none"
            aria-hidden
          >
            <path
              d="M4.37065 54.3724C2.23291 53.0514 0.931641 50.7177 0.931642 48.2048L0.931648 13.6473C0.931649 10.207 3.34948 7.24037 6.71907 6.54635L32.6597 1.20338C34.0239 0.922387 35.431 0.922394 36.7952 1.20338L62.7358 6.54635C66.1054 7.24037 68.5232 10.207 68.5232 13.6473L68.5232 48.2048C68.5232 50.7177 67.2219 53.0514 65.0842 54.3724L38.5384 70.7752C36.2027 72.2185 33.2521 72.2185 30.9164 70.7752L4.37065 54.3724Z"
              strokeWidth={1.5}
            />
          </svg>
          <div className="pos-a top-0 left-0 w-100 pb-1_25 px-0_5 pt-0_5">
            <div className="rating-text heading-2">{editorialRating}</div>
            <div className="scale pos-r small">out of {ratingScale}</div>
          </div>
        </div>
        {!hideTestedText && (
          <div
            className="bg-gray-lightest px-0_5 py-0_25 px-lg-0_25 px-xl-0_5 fw-medium small text-nowrap rounded-8 interaction-underline"
            style={{ letterSpacing: '1px' }}
          >
            edmunds{' '}
            <span className="fw-bold" style={{ letterSpacing: '3px' }}>
              TESTED
            </span>
          </div>
        )}
      </Tag>
    </div>
  );
}

RatingBadge.propTypes = {
  editorialRating: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  className: PropTypes.string,
  badgeContainerClassName: PropTypes.string,
  ratingScale: PropTypes.number,
  isScrollLink: PropTypes.bool,
  hideRatingText: PropTypes.bool,
  hideTestedText: PropTypes.bool,
  showExplanationTooltip: PropTypes.bool,
  ratingVersion: PropTypes.string,
  isScorecardV4: PropTypes.bool,
};
